import React from 'react';

// In-text linkifier for cross-references between Toronto cleaning service
// pages. Service-page copy mentions other services by name; rather than
// authoring every paragraph as JSX, we author plain strings and convert
// known phrases to anchor tags at render time.
//
// Phrase ordering matters: longer phrases must come first so that
// "post-construction commercial cleaning" matches before
// "post-construction cleaning" and "bathroom deep cleaning" matches
// before "deep cleaning". The list is hand-ordered from most-specific
// to least-specific.

const CLEANING_SERVICE_PHRASES = [
  ['post-construction commercial cleaning', 'post-construction-commercial-cleaning'],
  ['bathroom deep cleaning', 'bathroom-deep-cleaning'],
  ['kitchen deep cleaning', 'kitchen-deep-cleaning'],
  ['hardwood floor cleaning', 'hardwood-floor-cleaning'],
  ['tile and grout cleaning', 'tile-grout-cleaning'],
  ['post-construction cleaning', 'post-construction-cleaning'],
  ['post-party cleaning', 'post-party-cleaning'],
  ['smoke damage cleaning', 'smoke-damage-cleaning'],
  ['eco-friendly cleaning', 'eco-friendly-cleaning'],
  ['furnished rental cleaning', 'furnished-rental-cleaning'],
  ['pre-listing cleaning', 'pre-listing-cleaning'],
  ['same-day cleaning', 'same-day-cleaning'],
  ['eavestrough cleaning', 'eavestrough-cleaning'],
  ['medical office cleaning', 'medical-office-cleaning'],
  ['restaurant cleaning', 'restaurant-cleaning'],
  ['retail cleaning', 'retail-cleaning'],
  ['office cleaning', 'office-cleaning'],
  ['gym cleaning', 'gym-cleaning'],
  ['daycare cleaning', 'daycare-cleaning'],
  ['salon cleaning', 'salon-cleaning'],
  ['janitorial services', 'janitorial-services'],
  ['pet stain removal', 'pet-stain-removal'],
  ['hoarding cleanup', 'hoarding-cleanup'],
  ['garage cleanout', 'garage-cleanout'],
  ['junk removal', 'junk-removal'],
  ['basement cleaning', 'basement-cleaning'],
  ['balcony cleaning', 'balcony-cleaning'],
  ['nursery cleaning', 'nursery-cleaning'],
  ['senior cleaning', 'senior-cleaning'],
  ['holiday cleaning', 'holiday-cleaning'],
  ['spring cleaning', 'spring-cleaning'],
  ['move-out cleaning', 'move-out-cleaning'],
  ['move-in cleaning', 'move-in-cleaning'],
  ['Airbnb cleaning', 'airbnb-cleaning'],
  ['airbnb cleaning', 'airbnb-cleaning'],
  ['oven cleaning', 'oven-cleaning'],
  ['bbq cleaning', 'bbq-cleaning'],
  ['BBQ cleaning', 'bbq-cleaning'],
  ['upholstery cleaning', 'upholstery-cleaning'],
  ['mattress cleaning', 'mattress-cleaning'],
  ['pressure washing', 'pressure-washing'],
  ['recurring cleaning', 'recurring-cleaning'],
  ['carpet cleaning', 'carpet-cleaning'],
  ['window cleaning', 'window-cleaning'],
  ['apartment cleaning', 'apartment-cleaning'],
  ['condo cleaning', 'condo-cleaning'],
  ['studio cleaning', 'studio-cleaning'],
  ['house cleaning', 'house-cleaning'],
  ['deep cleaning', 'deep-cleaning'],
  ['disinfection', 'disinfection'],
  ['decluttering', 'decluttering'],
];

const escapeRegExp = string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Convert a plain-text paragraph into a React fragment where known
 * cleaning service phrases are wrapped in <a> tags pointing to the
 * corresponding Toronto service page. Self-references (matches against
 * the page's own slug) are left as plain text to avoid linking a page
 * to itself.
 */
export const linkifyCleaningServices = (text, currentSlug) => {
  if (typeof text !== 'string' || !text) {
    return text;
  }

  // Build a single regex with all phrases for one-pass scanning. Phrases
  // come pre-sorted longest-first so the regex matches the most specific
  // candidate at each position.
  const phrases = CLEANING_SERVICE_PHRASES.filter(([, slug]) => slug !== currentSlug);
  const pattern = new RegExp(`(${phrases.map(([phrase]) => escapeRegExp(phrase)).join('|')})`, 'g');

  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    const matchedPhrase = match[1];
    const start = match.index;
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    const lookup = CLEANING_SERVICE_PHRASES.find(([phrase]) => phrase === matchedPhrase);
    if (lookup) {
      parts.push(
        <a key={`linkified-${key++}`} href={`/cleaning/toronto/${lookup[1]}`}>
          {matchedPhrase}
        </a>
      );
    } else {
      parts.push(matchedPhrase);
    }
    lastIndex = start + matchedPhrase.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};
